import { useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { type ClaimStatus } from '@order/features-loyalty';

// ─────────────────────────────────────────────────────────────────────────────

export const useClaimStatusMessages = (props: UseClaimStatusMessagesProps) => {
  const { status, rewardName } = props;

  const { formatMessage } = useIntl();

  return useMemo(() => {
    return {
      header:
        status === 'claimed' && rewardName ? rewardName : formatMessage(headerMessages[status]),
      body: formatMessage(bodyMessages[status]),
      cta: formatMessage(messages.continueCta),
    };
  }, [formatMessage, rewardName, status]);
};

// ─── Messages ────────────────────────────────────────────────────────────────

const headerMessages = defineMessages<ClaimStatus>({
  claiming: {
    defaultMessage: 'Claiming your reward...',
    description: 'Rewards | Apply Reward | Claiming | Header',
  },
  claimed: {
    defaultMessage: 'Reward claimed!',
    description: 'Rewards | Apply Reward | Claimed | Header',
  },
  alreadyClaimed: {
    defaultMessage: 'This reward is currently not available',
    description: 'Rewards | Apply Reward | Already Claimed | Header',
  },
  newUserOnly: {
    defaultMessage: 'This reward is currently not available',
    description: 'Rewards | Apply Reward | New User Only | Header',
  },
  notEligible: {
    defaultMessage: 'This reward is currently not available',
    description: 'Rewards | Apply Reward | Not Eligible | Header',
  },
  expired: {
    defaultMessage: 'This reward is currently not available',
    description: 'Rewards | Apply Reward | Expired | Header',
  },
  unknownError: {
    defaultMessage: 'This reward is currently not available',
    description: 'Rewards | Apply Reward | Unknown Error | Header',
  },
});

const bodyMessages = defineMessages<ClaimStatus>({
  claiming: {
    defaultMessage: 'Just a moment...',
    description: 'Rewards | Apply Reward | Claiming | Body',
  },
  claimed: {
    defaultMessage:
      'Reward has been added to your account! Enjoy it on your next order. Rewards can be redeemed at checkout.',
    description: 'Rewards | Apply Reward | Claimed | Body',
  },
  alreadyClaimed: {
    defaultMessage:
      'This reward has already been claimed. You can not add multiple of this reward type.',
    description: 'Rewards | Apply Reward | Already Claimed | Body',
  },
  newUserOnly: {
    defaultMessage: 'This offer is only available for new Sweetgreen Loyalty members.',
    description: 'Rewards | Apply Reward | New User Only | Body',
  },
  notEligible: {
    defaultMessage: 'This offer is not available for your account or account type.',
    description: 'Rewards | Apply Reward | Not Eligible | Body',
  },
  expired: {
    defaultMessage: 'This offer has expired. This offer is no longer available.',
    description: 'Rewards | Apply Reward | Expired | Body',
  },
  unknownError: {
    defaultMessage: 'Something went wrong... We could not process your claim.',
    description: 'Rewards | Apply Reward | Unknown Error | Body',
  },
});

const messages = defineMessages({
  continueCta: {
    defaultMessage: 'Continue',
    description: 'Rewards | Apply Reward | CTA | Continue',
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type UseClaimStatusMessagesProps = {
  status: ClaimStatus;
  rewardName: string | undefined;
};
